import { EVENT_PLAYBACK_STATUS } from '../../constants/enums'
import { EventsTrait, EventBus, createEventBus } from '../../events/bus'
import { ChapterData } from '../../services/telemetry/video/events'
import { Interval, intervalsToDuration, getNonOverlappingIntervals } from '../../utils/time'
import { Chapter } from '../../services/telemetry/types'

import {
  VIDEO_PLAYBACK_CHAPTER_END,
  VIDEO_PLAYBACK_ENDED,
  VIDEO_PLAYER_UNMOUNTED,
  VIDEO_PLAYER_MOUNTED,
  VIDEO_PLAYBACK_CHAPTER_START
} from '../../events/types/video.events'

const TIMER = 10000

class Chapters extends EventsTrait {
  #data: Array<Chapter>
  #previous?: ChapterData
  #current?: ChapterData
  #timer: number
  #intervals: Array<Interval>
  #events: EventBus

  duration: number

  get events() {
    return this.#events
  }

  get current() {
    return this.#current
  }

  get previous() {
    return this.#previous
  }

  get intervals() {
    return this.#intervals
  }

  get list() {
    return this.#data ? [ ...this.#data ] : []
  }

  get length() {
    return this.list.length
  }

  get isEmpty() {
    return this.length === 0
  }

  get isLast() {
    return this.current?.id === this.data[this.length - 1].id
  }

  get data() {
    return this.list.map((chapter, index) => {
      const end = index < this.length - 1 ?
        this.list[index + 1].marker || this.duration : this.duration

      return {
        id: index,
        title: chapter.title,
        marker: chapter.marker,
        start: chapter.marker,
        end,
        progress: 0
      } as ChapterData
    })
  }

  constructor(data: Array<Chapter>, events = createEventBus(), duration: number) {
    super()

    this.#data = data
    this.#events = events

    this.#timer = NaN
    this.#intervals = []

    this.duration = duration

    const clear = () => clearTimeout(this.#timer)

    const chapterEnded = (status: EVENT_PLAYBACK_STATUS) => {
      if (this.previous && this.current) {
        this.events.dispatchEvent(VIDEO_PLAYBACK_CHAPTER_END(this.previous, this.current, status))
      }
    }

    this.on(VIDEO_PLAYBACK_ENDED, () => {
      clear()
      chapterEnded(EVENT_PLAYBACK_STATUS.COMPLETED)
    })

    this.on(VIDEO_PLAYER_MOUNTED, clear)
    this.on(VIDEO_PLAYER_UNMOUNTED, () => {
      clear()
      chapterEnded(EVENT_PLAYBACK_STATUS.SKIPPED)
    })

    this.#watch()
  }

  findByTime(time: number) {
    const chapter = this.data.find((item: ChapterData) => item.start <= time && item.end >= time) || null

    if (chapter) return {
      id: chapter.id,
      title: chapter.title,
      marker: chapter.marker,
      start: chapter.marker == null ? NaN : chapter.marker,
      end: chapter.end,
      maxTimestamp: time,
      progress: 0,
      duration: 0
    } as ChapterData
  }

  #watch(time: number = 0) {
    if (!this.isEmpty && this.duration > 0) {
      const chapter = this.findByTime(time)

      if (chapter) {
        this.#current = chapter
        this.#previous = chapter
      }

      clearTimeout(this.#timer)

      this.#timer = setInterval(() => {
        if (this.current && this.previous)
          this.events.dispatchEvent(VIDEO_PLAYBACK_CHAPTER_END(this.previous, this.current, EVENT_PLAYBACK_STATUS.PENDING))
      }, TIMER) as never as number
    }
  }

  public use(data: Array<Chapter>) {
    this.clear()

    this.#data = data

    this.#watch()
  }

  public clear() {
    this.#data = []
    this.#current = undefined
    this.#previous = undefined
    this.#intervals = []
  }

  public track(previousTime: number, time: number, reason: EVENT_PLAYBACK_STATUS) {

    if (!this.current || !this.previous) {
      this.#watch(time)

      if (!this.current || !this.previous) return

      this.events.dispatchEvent(VIDEO_PLAYBACK_CHAPTER_START(this.current))
    }

    if (this.intervals.length > 0) {
      this.intervals[this.intervals.length - 1].end = EVENT_PLAYBACK_STATUS.SKIPPED ? previousTime : time

      if (reason === EVENT_PLAYBACK_STATUS.SKIPPED) this.intervals.push({ start: time, end: time })
    } else this.intervals.push({ start: time, end: time })

    const mergedChapterIntervals = getNonOverlappingIntervals(this.intervals)
    const chapterPlaybackDuration = intervalsToDuration(mergedChapterIntervals)

    this.previous.maxTimestamp = Math.max(this.previous.maxTimestamp || 0, previousTime)
    this.previous.currentTimestamp = previousTime
    this.previous.progress = chapterPlaybackDuration * 100 / (this.previous.end - this.previous.start)
    this.previous.duration = chapterPlaybackDuration

    if (time < this.current.start || time >= this.current.end) {
      this.#current = this.findByTime(time)

      if (this.current) {
        this.events.dispatchEvent(VIDEO_PLAYBACK_CHAPTER_END(this.previous, this.current, reason))

        if (!this.isLast) {
          this.events.dispatchEvent(VIDEO_PLAYBACK_CHAPTER_START(this.current))
        }

        // if chapter was changed, we have to create a new Interval
        if (reason == EVENT_PLAYBACK_STATUS.SKIPPED) {
          this.#intervals = [{ start: time, end: time }]
        }
      }

      this.#previous = this.current
    }
  }
}

export { Chapters }
export type { Chapter }
